/**
 * Boards REST API (`/api/boards/…`, eval/MIRO_PLAN.md) on top of the shared
 * ApiClient — JWT storage, single-flight refresh and 401 retry come from
 * @redline/shared. The board site is same-origin, so the API base is ''.
 */

import { ApiClient } from '@redline/shared/api';
import type { BoardDriveStatus } from '../../shared/src/boardDrive';
import type { TemplateId } from './templates';

/**
 * Canvas role. Drive commenters can't change the canvas, so the API's
 * 'commenter' arrives here as 'viewer'; `access_role` keeps the full role.
 */
export type BoardRole = 'owner' | 'editor' | 'viewer';
export type AccessRole = BoardRole | 'commenter';

export interface Board {
  id: string;
  title: string;
  kind: 'excalidraw';
  template: TemplateId | string;
  owner_id: string;
  owner_name: string;
  role: BoardRole;
  created_at: string;
  updated_at: string;
  thumbnail_url: string | null;
  share_url: string | null;
  member_count: number;
  /** Share link on (view-only). */
  share_enabled: boolean;
  /** Where the board lives in Google Drive (its sharing decides access). */
  drive: BoardDriveStatus;
  access_role: AccessRole;
}

type RawBoard = Omit<Board, 'role' | 'access_role'> & { role: AccessRole };

/** API board → app board (commenters are read-only on the canvas). */
export function normalizeBoard(b: RawBoard): Board {
  return { ...b, access_role: b.role, role: b.role === 'commenter' ? 'viewer' : b.role };
}

export interface Asset {
  id: string;
  url: string;
  mime: string;
  width?: number;
  height?: number;
}

export const KIND = 'excalidraw' as const;

/** One client per page; '' = same origin (Vite proxies in dev, nginx in production). */
export const api = new ApiClient({ baseUrl: (import.meta.env.VITE_API_BASE as string | undefined) ?? '' });

const json = (method: string, data: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(data),
});

export interface BoardsApi {
  list(): Promise<Board[]>;
  /** List plus `refreshing`: the server is re-checking Drive-shared boards; fetch once more shortly. */
  listPage(): Promise<{ boards: Board[]; refreshing: boolean }>;
  create(title: string, template: TemplateId, driveFolderId?: string | null): Promise<Board>;
  get(id: string): Promise<Board>;
  rename(id: string, title: string): Promise<Board>;
  remove(id: string): Promise<void>;
  share(id: string, rotate?: boolean): Promise<{ token: string; url: string }>;
  join(token: string): Promise<Board>;
  uploadThumbnail(id: string, png: Blob, opts?: { keepalive?: boolean }): Promise<{ thumbnail_url: string } | void>;
  uploadAsset(id: string, file: Blob, filename: string): Promise<Asset>;
}

export function restBoardsApi(client: ApiClient = api): BoardsApi {
  const listPage = async () => {
    const r = await client.json<{ boards: RawBoard[]; refreshing?: boolean }>(`/api/boards/?kind=${KIND}`);
    return { boards: r.boards.map(normalizeBoard), refreshing: !!r.refreshing };
  };
  return {
    async list() {
      return (await listPage()).boards;
    },
    listPage,
    async create(title, template, driveFolderId) {
      return normalizeBoard(await client.json('/api/boards/', json('POST', { title, kind: KIND, template, drive_folder_id: driveFolderId || '' })));
    },
    async get(id) {
      return normalizeBoard(await client.json(`/api/boards/${id}/`));
    },
    async rename(id, title) {
      return normalizeBoard(await client.json(`/api/boards/${id}/`, json('PATCH', { title })));
    },
    async remove(id) {
      await client.request(`/api/boards/${id}/`, { method: 'DELETE' });
    },
    share(id, rotate = false) {
      return client.json(`/api/boards/${id}/share/${rotate ? '?rotate=1' : ''}`, { method: 'POST' });
    },
    async join(token) {
      return normalizeBoard(await client.json('/api/boards/join/', json('POST', { token })));
    },
    async uploadThumbnail(id, png, opts = {}) {
      const fd = new FormData();
      fd.append('file', png, 'thumbnail.png');
      if (opts.keepalive) {
        // Page is going away: a plain keepalive fetch (no refresh-retry possible).
        const token = client.tokens.access;
        await fetch(client.url(`/api/boards/${id}/thumbnail/`), {
          method: 'PUT',
          body: fd,
          keepalive: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return;
      }
      return client.json(`/api/boards/${id}/thumbnail/`, { method: 'PUT', body: fd });
    },
    uploadAsset(id, file, filename) {
      const fd = new FormData();
      fd.append('file', file, filename);
      return client.json(`/api/boards/${id}/assets/`, { method: 'POST', body: fd });
    },
  };
}
